import { inject, Injector, Injectable } from '@angular/core';
import { ComponentType } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SLIDE_PANEL_DATA, SLIDE_PANEL_REF, SlidePanelRef } from './slide-panel-tokens';

export interface SlidePanelConfig<D = unknown> {
  data?: D;
  title?: string;
  width?: string;
}

interface SlidePanelState {
  portal: ComponentPortal<unknown> | null;
  title: string;
  width: string;
}

@Injectable({ providedIn: 'root' })
export class SlidePanelService {
  private readonly injector = inject(Injector);
  private readonly _state$ = new BehaviorSubject<SlidePanelState>({
    portal: null,
    title: '',
    width: '520px',
  });

  readonly state$: Observable<SlidePanelState> = this._state$.asObservable();
  readonly isOpen$: Observable<boolean> = this._state$.pipe(
    map((s) => s.portal !== null)
  );

  open<T>(component: ComponentType<T>, config: SlidePanelConfig = {}): void {
    const { data, title = '', width = '520px' } = config;
    const panelRef: SlidePanelRef = {
      close: () => this.close(),
    };
    const injector = Injector.create({
      providers: [
        { provide: SLIDE_PANEL_DATA, useValue: data ?? null },
        { provide: SLIDE_PANEL_REF, useValue: panelRef },
      ],
      parent: this.injector,
    });
    const portal = new ComponentPortal(component, null, injector);
    this._state$.next({ portal, title, width });
  }

  close(): void {
    this._state$.next({
      portal: null,
      title: '',
      width: '520px',
    });
  }
}
